import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
	X,
	ScanLine,
	Barcode,
	Radio,
	ClipboardList,
	Printer,
	Mail,
	Loader as Loader2,
	Trash2,
	CheckCircle2,
	AlertTriangle,
} from 'lucide-react'
import { Button } from '../ui/Button'
import { HapticFeedback } from '../ui/HapticFeedback'
import { QuantityStepper } from '../ui/QuantityStepper'
import { supabase } from '../../lib/supabase'
import { classifyScanValue } from '../../utils/scanClassify'
import { resolveWedgeScan } from '../../utils/resolveWedgeScan'
import { fetchCompanyProfile, type CompanyProfile } from '../../lib/companyProfile'
import {
	buildShopStockCountMatrix,
	buildShopStockCountReportHtml,
	printShopStockCountReport,
	type ShopStockCountReportLine,
} from './shop-stock-count-report'
import { StockCountItemPicker } from './stock-count-item-picker'
import type { VariantAddSelection } from '../inhouse/product-variant-matrix-modal'
import type { ShopStockCountMode, ShopStockCountRecord } from '../../hooks/useShopStockCounts'
import {
	fetchWorkerContacts,
	isEmailConfigured,
	recordShopStockCountDelivery,
	sendShopStockCountReport,
	type WorkerContactOption,
} from '../../lib/shopStockCountEmail'

type Phase = 'mode' | 'counting' | 'report'
type ScanFeedback = { ok: boolean; message: string } | null

interface StockCountSessionModalProps {
	adminId: string | null
	userId: string | null
	userName: string | null
	userEmail: string | null
	/** When continuing, pass the existing open count. */
	existing?: ShopStockCountRecord | null
	onClose: () => void
	onComplete: () => void
}

export function StockCountSessionModal({
	adminId,
	userId,
	userName,
	userEmail,
	existing = null,
	onClose,
	onComplete,
}: StockCountSessionModalProps) {
	const [phase, setPhase] = useState<Phase>(existing ? 'counting' : 'mode')
	const [mode, setMode] = useState<ShopStockCountMode>(existing?.mode ?? 'one_by_one')
	const [countId, setCountId] = useState<string | null>(existing?.count_id ?? null)
	const [countNumber, setCountNumber] = useState(existing?.count_number ?? '')
	const [locationName, setLocationName] = useState(existing?.location_name ?? 'Shop')
	const [lines, setLines] = useState<ShopStockCountReportLine[]>(existing?.lines ?? [])
	const [starting, setStarting] = useState(false)
	const [submitting, setSubmitting] = useState(false)
	const [actionError, setActionError] = useState<string | null>(null)
	const [scanFeedback, setScanFeedback] = useState<ScanFeedback>(null)
	const [busyLineId, setBusyLineId] = useState<string | null>(null)

	const [company, setCompany] = useState<CompanyProfile | null>(null)
	const [reportHtml, setReportHtml] = useState<string | null>(null)
	const [emailPossible, setEmailPossible] = useState(true)
	const [contacts, setContacts] = useState<WorkerContactOption[]>([])
	const [recipient, setRecipient] = useState(userEmail ?? '')
	const [sending, setSending] = useState(false)
	const [sentTo, setSentTo] = useState<string | null>(null)

	const scanBufferRef = useRef('')
	const scanTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const lastScanValueRef = useRef('')
	const lastScanTimeRef = useRef(0)
	const processScanRef = useRef<(raw: string) => Promise<void>>()

	const addedQtyByProductId = useMemo(() => {
		const map: Record<string, number> = {}
		for (const line of lines) {
			if (line.product_id) map[line.product_id] = line.counted_qty
		}
		return map
	}, [lines])

	const matrix = useMemo(() => buildShopStockCountMatrix(lines), [lines])
	const totalUnits = matrix.totalUnits

	useEffect(() => {
		if (!adminId) return
		let cancelled = false
		void fetchCompanyProfile(adminId).then((profile) => {
			if (!cancelled) setCompany(profile)
		})
		return () => {
			cancelled = true
		}
	}, [adminId])

	const mergeUpdatedLines = useCallback((updated: ShopStockCountReportLine[]) => {
		setLines((prev) => {
			const next = [...prev]
			for (const line of updated) {
				const idx = next.findIndex(
					(l) =>
						(line.id && l.id === line.id) ||
						(line.product_id && l.product_id === line.product_id)
				)
				if (idx >= 0) next[idx] = { ...next[idx], ...line }
				else next.unshift(line)
			}
			return next
		})
	}, [])

	const recordScan = useCallback(
		async (
			payload: { product_id: string; qty: number }[],
			scanSource: 'manual' | 'barcode' | 'epc',
			epc?: string | null
		) => {
			if (!adminId || !countId) return { success: false as const, error: 'Count not open' }

			const { data, error } = await supabase.rpc('record_shop_stock_count_scan', {
				p_admin_id: adminId,
				p_count_id: countId,
				p_user_id: userId,
				p_lines: payload,
				p_scan_source: scanSource,
				p_epc: epc ?? null,
			})
			if (error) throw error
			return data as {
				success: boolean
				duplicate?: boolean
				added?: number
				lines?: ShopStockCountReportLine[]
				error?: string
			}
		},
		[adminId, countId, userId]
	)

	const processScan = useCallback(
		async (raw: string) => {
			if (!adminId || !countId || phase !== 'counting') return
			const value = raw.trim()
			if (!value) return

			try {
				const kind = classifyScanValue(value)

				if (kind === 'epc') {
					const result = await resolveWedgeScan(adminId, value)
					if (result.kind === 'epc_error' || result.kind === 'unknown') {
						HapticFeedback.error()
						setScanFeedback({ ok: false, message: result.message || 'Unknown tag' })
						return
					}
					if (result.kind !== 'epc') {
						HapticFeedback.error()
						setScanFeedback({ ok: false, message: 'Could not resolve UHF tag' })
						return
					}

					const res = await recordScan(
						[{ product_id: result.item.id, qty: 1 }],
						'epc',
						result.value
					)
					if (!res.success) {
						HapticFeedback.error()
						setScanFeedback({ ok: false, message: res.error || 'Scan failed' })
						return
					}
					if (res.duplicate) {
						HapticFeedback.medium()
						setScanFeedback({ ok: true, message: `Already counted: ${result.item.name || result.item.sku}` })
						return
					}
					if (res.lines) mergeUpdatedLines(res.lines)
					HapticFeedback.success()
					setScanFeedback({
						ok: true,
						message: `+1 ${result.item.name || result.item.sku || 'item'}`,
					})
					return
				}

				if (kind === 'barcode') {
					const { data: products, error } = await supabase
						.from('products')
						.select('id, name, sku, barcode, reference_code')
						.eq('admin_id', adminId)
						.or(`barcode.eq.${value},sku.eq.${value},reference_code.eq.${value}`)
						.limit(5)

					if (error) throw error
					if (!products || products.length === 0) {
						HapticFeedback.error()
						setScanFeedback({ ok: false, message: `No product for ${value}` })
						return
					}
					if (products.length > 1) {
						HapticFeedback.error()
						setScanFeedback({ ok: false, message: `Ambiguous barcode ${value}` })
						return
					}

					const product = products[0]
					const res = await recordScan([{ product_id: product.id, qty: 1 }], 'barcode')
					if (!res.success) {
						HapticFeedback.error()
						setScanFeedback({ ok: false, message: res.error || 'Scan failed' })
						return
					}
					if (res.lines) mergeUpdatedLines(res.lines)
					HapticFeedback.success()
					setScanFeedback({
						ok: true,
						message: `+1 ${product.name || product.sku || value}`,
					})
					return
				}

				HapticFeedback.error()
				setScanFeedback({ ok: false, message: `Unrecognized scan: ${value}` })
			} catch (err) {
				HapticFeedback.error()
				setScanFeedback({
					ok: false,
					message: err instanceof Error ? err.message : 'Scan failed',
				})
			}
		},
		[adminId, countId, phase, recordScan, mergeUpdatedLines]
	)

	processScanRef.current = processScan

	useEffect(() => {
		if (phase !== 'counting' || (mode !== 'barcode' && mode !== 'uhf')) return

		const handleKeyPress = (e: KeyboardEvent) => {
			const target = e.target
			if (
				target instanceof HTMLInputElement ||
				target instanceof HTMLTextAreaElement ||
				target instanceof HTMLSelectElement
			) {
				return
			}

			if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current)
			const now = Date.now()

			if (e.key === 'Enter') {
				e.preventDefault()
				const buffer = scanBufferRef.current
				if (buffer.length === 0) return

				const dedupeMs = classifyScanValue(buffer) === 'epc' ? 1200 : 150
				const isDuplicate =
					now - lastScanTimeRef.current < dedupeMs && buffer === lastScanValueRef.current

				if (!isDuplicate) {
					lastScanTimeRef.current = now
					lastScanValueRef.current = buffer
					void processScanRef.current?.(buffer)
				}
				scanBufferRef.current = ''
				return
			}

			if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
				scanBufferRef.current += e.key
				scanTimeoutRef.current = setTimeout(() => {
					scanBufferRef.current = ''
				}, 300)
			}
		}

		window.addEventListener('keypress', handleKeyPress)
		return () => {
			window.removeEventListener('keypress', handleKeyPress)
			if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current)
		}
	}, [phase, mode])

	const handleStart = async (chosen: ShopStockCountMode) => {
		if (!adminId || starting) return
		setStarting(true)
		setActionError(null)
		try {
			const { data, error } = await supabase.rpc('start_shop_stock_count', {
				p_admin_id: adminId,
				p_user_id: userId,
				p_mode: chosen,
				p_location_id: null,
			})
			if (error) throw error

			const res = data as {
				success: boolean
				already_open?: boolean
				count_id?: string
				count_number?: string
				mode?: ShopStockCountMode
				location_name?: string | null
				error?: string
			}

			if (!res.success) {
				setActionError(res.error || 'Could not start stock count')
				return
			}

			setCountId(res.count_id ?? null)
			setCountNumber(res.count_number ?? '')
			setMode((res.mode as ShopStockCountMode) || chosen)
			setLocationName(res.location_name || 'Shop')

			if (res.already_open && res.count_id) {
				const { data: rows } = await supabase.rpc('get_shop_stock_counts', {
					p_admin_id: adminId,
					p_status: 'open',
					p_from: null,
					p_to: null,
					p_limit: 1,
				})
				const open = (rows as ShopStockCountRecord[] | null)?.[0]
				if (open) setLines(open.lines ?? [])
			}

			setPhase('counting')
		} catch (err) {
			setActionError(err instanceof Error ? err.message : 'Could not start stock count')
		} finally {
			setStarting(false)
		}
	}

	const handlePickerAdd = async (selections: VariantAddSelection[]) => {
		if (selections.length === 0) return
		try {
			const res = await recordScan(
				selections.map((s) => ({ product_id: s.productId, qty: s.quantity })),
				'manual'
			)
			if (!res.success) {
				setActionError(res.error || 'Could not add items')
				return
			}
			if (res.lines) mergeUpdatedLines(res.lines)
			HapticFeedback.success()
		} catch (err) {
			setActionError(err instanceof Error ? err.message : 'Could not add items')
		}
	}

	const handleSetQty = async (line: ShopStockCountReportLine, qty: number) => {
		if (!adminId || !countId || !line.id) return
		setBusyLineId(line.id)
		try {
			const { data, error } = await supabase.rpc('set_shop_stock_count_line_qty', {
				p_admin_id: adminId,
				p_count_id: countId,
				p_line_id: line.id,
				p_user_id: userId,
				p_counted_qty: qty,
			})
			if (error) throw error
			const res = data as { success: boolean; counted_qty?: number; error?: string }
			if (!res.success) {
				setActionError(res.error || 'Could not update quantity')
				return
			}
			setLines((prev) =>
				prev.map((l) => (l.id === line.id ? { ...l, counted_qty: res.counted_qty ?? qty } : l))
			)
		} catch (err) {
			setActionError(err instanceof Error ? err.message : 'Could not update quantity')
		} finally {
			setBusyLineId(null)
		}
	}

	const handleRemoveLine = async (line: ShopStockCountReportLine) => {
		if (!adminId || !countId || !line.id) return
		setBusyLineId(line.id)
		try {
			const { data, error } = await supabase.rpc('remove_shop_stock_count_line', {
				p_admin_id: adminId,
				p_count_id: countId,
				p_line_id: line.id,
				p_user_id: userId,
			})
			if (error) throw error
			const res = data as { success: boolean; error?: string }
			if (!res.success) {
				setActionError(res.error || 'Could not remove line')
				return
			}
			setLines((prev) => prev.filter((l) => l.id !== line.id))
		} catch (err) {
			setActionError(err instanceof Error ? err.message : 'Could not remove line')
		} finally {
			setBusyLineId(null)
		}
	}

	const handleFinish = async () => {
		if (!adminId || !countId || submitting) return
		if (totalUnits <= 0) {
			setActionError('Add at least one item before finishing')
			return
		}

		setSubmitting(true)
		setActionError(null)
		try {
			const { data, error } = await supabase.rpc('submit_shop_stock_count', {
				p_admin_id: adminId,
				p_count_id: countId,
				p_user_id: userId,
				p_notes: null,
			})
			if (error) throw error
			const res = data as {
				success: boolean
				count_number?: string
				submitted_at?: string
				error?: string
			}
			if (!res.success) {
				setActionError(res.error || 'Could not finish stock count')
				return
			}

			const submittedAt = res.submitted_at || new Date().toISOString()
			const html = buildShopStockCountReportHtml({
				countNumber: res.count_number || countNumber,
				mode,
				locationName,
				startedByName: userName,
				submittedByName: userName,
				submittedAt,
				lines,
				company,
			})
			setReportHtml(html)
			setCountNumber(res.count_number || countNumber)

			const [configured, workerContacts] = await Promise.all([
				isEmailConfigured(adminId),
				fetchWorkerContacts(adminId),
			])
			setEmailPossible(configured)
			setContacts(workerContacts)
			if (!recipient && userEmail) setRecipient(userEmail)

			setPhase('report')
		} catch (err) {
			setActionError(err instanceof Error ? err.message : 'Could not finish stock count')
		} finally {
			setSubmitting(false)
		}
	}

	const handlePrint = () => {
		if (!reportHtml || !adminId || !countId) return
		printShopStockCountReport(reportHtml)
		void recordShopStockCountDelivery({ adminId, countId, printed: true })
	}

	const handleSend = async () => {
		if (!adminId || !countId || !reportHtml || !recipient || sending) return
		setSending(true)
		setActionError(null)
		try {
			await sendShopStockCountReport({
				adminId,
				to: recipient,
				reportHtml,
				summary: {
					countNumber,
					locationName,
					countedByName: userName,
					lines,
				},
			})
			await recordShopStockCountDelivery({ adminId, countId, to: recipient })
			setSentTo(recipient)
		} catch (err) {
			const message = err instanceof Error ? err.message : 'Email failed'
			setActionError(message)
			await recordShopStockCountDelivery({
				adminId,
				countId,
				to: recipient,
				error: message,
			})
		} finally {
			setSending(false)
		}
	}

	const handleCancel = async () => {
		if (!adminId || !countId) {
			onClose()
			return
		}
		if (!window.confirm('Cancel this stock count? Progress will be kept as cancelled.')) return
		try {
			await supabase.rpc('cancel_shop_stock_count', {
				p_admin_id: adminId,
				p_count_id: countId,
				p_user_id: userId,
			})
			onComplete()
			onClose()
		} catch (err) {
			setActionError(err instanceof Error ? err.message : 'Could not cancel')
		}
	}

	const isScanner = mode === 'barcode' || mode === 'uhf'

	return (
		<div className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-gray-900/70 backdrop-blur-sm">
			<div className="w-full h-full sm:h-auto sm:max-h-[92vh] sm:max-w-4xl bg-white dark:bg-gray-900 sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden">
				<header className="flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-700">
					<div>
						<h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
							{phase === 'mode'
								? 'New stock count'
								: phase === 'counting'
								? countNumber || 'Counting…'
								: `Report ${countNumber}`}
						</h2>
						<p className="text-xs text-gray-500 mt-0.5">
							{phase === 'mode' && 'Choose how you will count'}
							{phase === 'counting' &&
								`${locationName} · ${totalUnits} unit${totalUnits === 1 ? '' : 's'} · ${
									isScanner ? 'Scanner running until Finish' : 'One by one'
								}`}
							{phase === 'report' && 'Print or email — inventory was not changed'}
						</p>
					</div>
					<button
						type="button"
						onClick={() => {
							if (phase === 'report') {
								onComplete()
								onClose()
								return
							}
							onClose()
						}}
						className="p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
						aria-label="Close"
					>
						<X className="h-5 w-5" />
					</button>
				</header>

				{actionError && (
					<div className="mx-4 sm:mx-6 mt-3 rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 text-sm flex items-start gap-2">
						<AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
						<span>{actionError}</span>
					</div>
				)}

				<div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 min-h-0">
					{phase === 'mode' && (
						<div className="grid gap-3 sm:grid-cols-3">
							<button
								type="button"
								disabled={starting}
								onClick={() => void handleStart('barcode')}
								className="flex flex-col items-start gap-3 p-5 rounded-2xl border-2 border-gray-200 dark:border-gray-700 hover:border-teal-500 hover:bg-teal-50/50 dark:hover:bg-teal-900/20 text-left transition"
							>
								<Barcode className="h-8 w-8 text-teal-700" />
								<div>
									<div className="font-semibold text-gray-900 dark:text-gray-100">Barcode scanner</div>
									<div className="text-sm text-gray-500 mt-1">
										Keeps scanning until you click Finish
									</div>
								</div>
							</button>
							<button
								type="button"
								disabled={starting}
								onClick={() => void handleStart('uhf')}
								className="flex flex-col items-start gap-3 p-5 rounded-2xl border-2 border-gray-200 dark:border-gray-700 hover:border-teal-500 hover:bg-teal-50/50 dark:hover:bg-teal-900/20 text-left transition"
							>
								<Radio className="h-8 w-8 text-teal-700" />
								<div>
									<div className="font-semibold text-gray-900 dark:text-gray-100">UHF scanner</div>
									<div className="text-sm text-gray-500 mt-1">
										Each tag counted once — re-reads are ignored
									</div>
								</div>
							</button>
							<button
								type="button"
								disabled={starting}
								onClick={() => void handleStart('one_by_one')}
								className="flex flex-col items-start gap-3 p-5 rounded-2xl border-2 border-gray-200 dark:border-gray-700 hover:border-teal-500 hover:bg-teal-50/50 dark:hover:bg-teal-900/20 text-left transition"
							>
								<ClipboardList className="h-8 w-8 text-teal-700" />
								<div>
									<div className="font-semibold text-gray-900 dark:text-gray-100">One by one</div>
									<div className="text-sm text-gray-500 mt-1">
										Search and pick color × size like In-house Shipment
									</div>
								</div>
							</button>
							{starting && (
								<div className="sm:col-span-3 flex items-center gap-2 text-sm text-gray-500">
									<Loader2 className="h-4 w-4 animate-spin" /> Starting…
								</div>
							)}
						</div>
					)}

					{phase === 'counting' && (
						<div className="grid gap-4 lg:grid-cols-2 h-full min-h-0">
							<div className="min-h-0 flex flex-col gap-3">
								{isScanner ? (
									<div className="rounded-2xl border border-teal-200 dark:border-teal-800 bg-teal-50/60 dark:bg-teal-900/20 p-5">
										<div className="flex items-center gap-3">
											<ScanLine className="h-7 w-7 text-teal-700" />
											<div>
												<div className="font-semibold text-teal-900 dark:text-teal-100">
													Scanner active
												</div>
												<div className="text-sm text-teal-800/80 dark:text-teal-200/80">
													{mode === 'uhf' ? 'Sweep UHF tags' : 'Scan barcodes'}. Both barcode and
													UHF are accepted. Does not stop until Finish.
												</div>
											</div>
										</div>
										{scanFeedback && (
											<div
												className={`mt-4 rounded-lg px-3 py-2 text-sm font-medium ${
													scanFeedback.ok
														? 'bg-emerald-100 text-emerald-900'
														: 'bg-red-100 text-red-900'
												}`}
											>
												{scanFeedback.message}
											</div>
										)}
									</div>
								) : (
									<div className="flex-1 min-h-[280px]">
										<StockCountItemPicker
											adminId={adminId}
											addedQtyByProductId={addedQtyByProductId}
											onAdd={(selections) => void handlePickerAdd(selections)}
										/>
									</div>
								)}
							</div>

							<div className="min-h-0 flex flex-col">
								<div className="flex items-center justify-between mb-2">
									<h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
										Counted ({lines.length})
									</h3>
									<span className="text-sm font-bold text-teal-700">{totalUnits} units</span>
								</div>
								<div className="flex-1 overflow-y-auto space-y-2 max-h-[50vh] lg:max-h-none">
									{lines.length === 0 ? (
										<p className="text-sm text-gray-400 italic py-8 text-center">
											Nothing counted yet
										</p>
									) : (
										lines.map((line) => (
											<div
												key={line.id || line.product_id || line.sku}
												className="flex items-center gap-3 rounded-xl border border-gray-200 dark:border-gray-700 p-3"
											>
												<div className="flex-1 min-w-0">
													<div className="font-medium text-sm text-gray-900 dark:text-gray-100 truncate">
														{line.product_name || line.sku || 'Item'}
													</div>
													<div className="text-xs text-gray-500 truncate">
														{[line.color_name, line.size_name].filter(Boolean).join(' / ') ||
															line.sku ||
															'—'}
													</div>
												</div>
												<QuantityStepper
													value={line.counted_qty}
													onChange={(val) => void handleSetQty(line, val)}
													min={0}
													max={99999}
													size="sm"
													disabled={busyLineId === line.id}
												/>
												<button
													type="button"
													onClick={() => void handleRemoveLine(line)}
													disabled={busyLineId === line.id}
													className="p-2 text-gray-400 hover:text-red-600"
													aria-label="Remove"
												>
													<Trash2 className="h-4 w-4" />
												</button>
											</div>
										))
									)}
								</div>
							</div>
						</div>
					)}

					{phase === 'report' && reportHtml && (
						<div className="space-y-4">
							<div className="rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-900 px-4 py-3 text-sm flex items-center gap-2">
								<CheckCircle2 className="h-5 w-5" />
								Count saved. Inventory was not changed.
							</div>

							<div className="grid sm:grid-cols-3 gap-3">
								<div className="rounded-xl border border-teal-200 bg-teal-50 p-4 text-center">
									<div className="text-[10px] uppercase tracking-wide font-bold text-teal-800">
										Units
									</div>
									<div className="text-2xl font-extrabold text-teal-900">{matrix.totalUnits}</div>
								</div>
								<div className="rounded-xl border border-teal-200 bg-teal-50 p-4 text-center">
									<div className="text-[10px] uppercase tracking-wide font-bold text-teal-800">
										Products
									</div>
									<div className="text-2xl font-extrabold text-teal-900">{matrix.totalSkus}</div>
								</div>
								<div className="rounded-xl border border-teal-200 bg-teal-50 p-4 text-center">
									<div className="text-[10px] uppercase tracking-wide font-bold text-teal-800">
										Lines
									</div>
									<div className="text-2xl font-extrabold text-teal-900">{matrix.totalLines}</div>
								</div>
							</div>

							<div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
								<table className="min-w-full text-sm">
									<thead className="bg-teal-50 dark:bg-teal-900/30">
										<tr>
											<th className="px-3 py-2 text-left">Product</th>
											<th className="px-3 py-2 text-left">Color</th>
											{matrix.sizeColumns.map((size) => (
												<th key={size} className="px-2 py-2 text-center">
													{size}
												</th>
											))}
											<th className="px-3 py-2 text-center">Total</th>
										</tr>
									</thead>
									<tbody>
										{matrix.groups.map((group) =>
											group.colors.map((color, idx) => (
												<tr
													key={`${group.productName}-${color.colorId}`}
													className="border-t border-gray-100 dark:border-gray-800"
												>
													{idx === 0 && (
														<td
															className="px-3 py-2 font-medium align-top"
															rowSpan={group.colors.length}
														>
															{group.productName}
														</td>
													)}
													<td className="px-3 py-2">{color.colorName}</td>
													{matrix.sizeColumns.map((size) => (
														<td key={size} className="px-2 py-2 text-center font-semibold">
															{color.sizeQuantities[size] || '·'}
														</td>
													))}
													<td className="px-3 py-2 text-center font-bold">{color.totalQty}</td>
												</tr>
											))
										)}
									</tbody>
								</table>
							</div>

							{!emailPossible && (
								<div className="rounded-lg border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2 text-sm">
									Email is not set up for this company. Use Print, or ask an admin to configure Brevo
									in Settings.
								</div>
							)}

							{emailPossible && (
								<div className="space-y-2">
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
										Send report to
									</label>
									<input
										type="email"
										value={recipient}
										onChange={(e) => setRecipient(e.target.value)}
										className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
										placeholder="email@example.com"
									/>
									{contacts.length > 0 && (
										<select
											className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
											value=""
											onChange={(e) => {
												if (e.target.value) setRecipient(e.target.value)
											}}
										>
											<option value="">Or pick a worker contact…</option>
											{contacts.map((c) => (
												<option key={c.id} value={c.email}>
													{c.name} ({c.email})
												</option>
											))}
										</select>
									)}
									{sentTo && (
										<p className="text-sm text-emerald-700">Sent to {sentTo}</p>
									)}
								</div>
							)}
						</div>
					)}
				</div>

				<footer className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
					{phase === 'counting' ? (
						<>
							<Button variant="secondary" onClick={() => void handleCancel()}>
								Cancel count
							</Button>
							<div className="flex gap-2">
								<Button variant="secondary" onClick={onClose}>
									Save &amp; close
								</Button>
								<Button
									variant="primary"
									onClick={() => void handleFinish()}
									disabled={submitting || totalUnits <= 0}
								>
									{submitting ? (
										<>
											<Loader2 className="h-4 w-4 animate-spin mr-2" /> Finishing…
										</>
									) : (
										'Finish'
									)}
								</Button>
							</div>
						</>
					) : phase === 'report' ? (
						<>
							<Button
								variant="secondary"
								onClick={() => {
									onComplete()
									onClose()
								}}
							>
								Done
							</Button>
							<div className="flex gap-2">
								<Button variant="secondary" onClick={handlePrint}>
									<Printer className="h-4 w-4 mr-2" /> Print
								</Button>
								{emailPossible && (
									<Button
										variant="primary"
										onClick={() => void handleSend()}
										disabled={sending || !recipient}
									>
										{sending ? (
											<>
												<Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending…
											</>
										) : (
											<>
												<Mail className="h-4 w-4 mr-2" /> Email
											</>
										)}
									</Button>
								)}
							</div>
						</>
					) : (
						<Button variant="secondary" onClick={onClose}>
							Close
						</Button>
					)}
				</footer>
			</div>
		</div>
	)
}
